import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, authImageUrl } from "@/lib/api";
import { ArrowLeft, Building2, Users, Crown, MapPin, Layers } from "lucide-react";
import { Laurel } from "@/components/LaurelBadge";

const Avatar = ({ m, size = "w-12 h-12" }) => {
  if (!m) return null;
  return m.photo_url ? (
    <img src={authImageUrl(m.photo_url)} alt={m.cognome}
      className={`${size} rounded-md object-cover border border-gold/40 shrink-0`}
      onError={(e) => { e.target.style.display = "none"; }} />
  ) : (
    <div className={`${size} rounded-md bg-navy flex items-center justify-center border border-gold/40 shrink-0`}>
      <span className="font-display text-gold font-bold">
        {(m.nome?.[0] || "").toUpperCase()}{(m.cognome?.[0] || "").toUpperCase()}
      </span>
    </div>
  );
};

export default function ClubDetail() {
  const { club } = useParams();
  const decoded = decodeURIComponent(club);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/org/clubs/${encodeURIComponent(decoded)}`).then((r) => setData(r.data));
  }, [decoded]);

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="club-detail">
      <Link to="/distretto" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-navy-hover transition-colors" data-testid="back-to-distretto">
        <ArrowLeft className="w-4 h-4" /> Torna al Distretto
      </Link>

      {!data ? (
        <div className="text-center py-16 text-navy">Caricamento…</div>
      ) : (
        <>
          {/* Header club — stile coerente con l'header delle divisioni */}
          <div className="bg-navy text-white rounded-lg overflow-hidden" data-testid="club-header">
            <div className="px-6 py-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
              <div className="min-w-0">
                <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-gold flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Club
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1 leading-tight">{data.club}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-white/70 mt-2">
                  {data.division && (
                    <span className="flex items-center gap-1.5"><Layers className="w-4 h-4 text-gold" />{data.division}</span>
                  )}
                  <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-gold" />{data.members_count - data.honorary_count} soci</span>
                  {data.honorary_count > 0 && (
                    <span className="flex items-center gap-1.5" data-testid="club-honorary-count"><Laurel className="w-4 h-4 text-gold" />+ {data.honorary_count} soci onorari</span>
                  )}
                </div>
              </div>
              {data.president ? (
                <Link to={`/soci/${data.president.id}`} data-testid="club-president-link"
                  className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-md px-4 py-3 transition-colors shrink-0">
                  <Avatar m={data.president} />
                  <div className="text-left leading-tight">
                    <p className="text-[10px] uppercase tracking-wider text-gold font-bold flex items-center gap-1"><Crown className="w-3 h-3" />Presidente</p>
                    <p className="text-sm font-semibold">{data.president.nome} {data.president.cognome}</p>
                  </div>
                </Link>
              ) : (
                <span className="text-xs text-white/50 italic flex items-center gap-1 shrink-0"><Crown className="w-4 h-4" />Presidente non indicato</span>
              )}
            </div>
          </div>

          {/* Elenco soci in ordine alfabetico */}
          <section>
            <div className="flex items-center gap-2 mb-5">
              <Users className="w-5 h-5 text-gold" />
              <h2 className="font-display text-2xl font-semibold text-navy">Soci del Club <span className="text-muted-foreground text-base font-sans">({data.members_count - data.honorary_count}{data.honorary_count > 0 ? ` + ${data.honorary_count} onorari` : ""})</span></h2>
            </div>

            {data.members.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground border border-dashed border-navy/15 rounded-lg bg-white/50" data-testid="club-no-members">
                Nessun socio in elenco per questo club.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {data.members.map((m) => {
                  const districtLabel = (m.district_roles && m.district_roles[0])
                    || (m.lieutenant_of && m.lieutenant_of.length ? "Luogotenente Governatore" : "");
                  return (
                  <Link key={m.id} to={`/soci/${m.id}`} data-testid={`club-member-${m.id}`}
                    className="group bg-white rounded-lg border border-navy/10 p-5 flex gap-4 items-center hover:shadow-md hover:-translate-y-1 transition-transform transition-shadow duration-200">
                    <Avatar m={m} size="w-14 h-14" />
                    <div className="min-w-0">
                      <p className="font-semibold text-navy-deep truncate group-hover:text-navy">{m.cognome} {m.nome}</p>
                      {m.carica && <p className="text-xs uppercase tracking-wider text-gold font-bold truncate">{m.carica}</p>}
                      {m.onorario && (
                        <p className="text-xs uppercase tracking-wider text-gold font-bold truncate flex items-center gap-1" data-testid={`club-honorary-role-${m.id}`}>
                          <Laurel className="w-3.5 h-3.5 shrink-0" />Socio Onorario
                        </p>
                      )}
                      {districtLabel && <p className="text-xs uppercase tracking-wider text-navy font-bold truncate">{districtLabel}</p>}
                      {m.divisione && (
                        <p className="text-sm text-muted-foreground flex items-start gap-1 mt-1">
                          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{m.divisione}</span>
                        </p>
                      )}
                    </div>
                  </Link>
                );})}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
